// Dumper Hourmeters types — RS-1338. Mirror retri-api/docs/contracts/dumperHourmeters.json

export type DumperSource = 'DRIVER' | 'RESIDENT_QR' | 'MANUAL'
export type DumperStatus = 'PENDIENTE' | 'FIRMADO' | 'RECHAZADO' | 'REGISTRADO' | 'EDITADO'
export type DumperSignMode = 'IN_PERSON' | 'OTP'
export type DumperRemisionSource = 'AUTO' | 'MANUAL'

export interface DumperHourmeter {
  id: number
  source: DumperSource
  status: DumperStatus
  // Discriminator dealId (Retri-managed) XOR operationId+transportAssetId (SaaS aliados).
  // RS-1338 v4: materialAssetId eliminado; material info via transportAsset.conditionMaterialId.
  dealId?: number | null
  transportAssetId?: number | null
  operationId?: number | null
  tripDate: string
  remisionNumber: string
  remisionSource: DumperRemisionSource
  realM3: number
  realTon?: number | null
  tripCount?: number
  distanceKm?: number | null
  materialType?: string | null
  snapshotIncomeRate?: number | null
  snapshotExpenseRate?: number | null
  snapshotMaterialRate?: number | null
  incomeRateOverridden?: boolean
  expenseRateOverridden?: boolean
  materialRateOverridden?: boolean
  operatorId: number
  signMode?: DumperSignMode | null
  residentId?: number | null
  registeredByResidentId?: number | null
  residentObservation?: string | null
  rejectReason?: string | null
  signedAt?: string | null
  photoUrl?: string | null
  editedFromId?: number | null
  createdAt: string
  updatedAt?: string
  deletedAt?: string | null
  // Joins (findOne)
  clientName?: string
  operatorName?: string
  operatorRole?: 'DRIVER' | 'MACHINE_OPERATOR' | 'BOTH' | null
  // Grupo de condiciones (RS-1338): id + nombre compuesto + foto obligatoria.
  conditionGroupId?: number | null
  groupName?: string | null
  photoRequired?: boolean
}

export interface CreateDumperHourmeterPayload {
  /**
   * La ruta que cobra el viaje. Ausente = el grupo por defecto del equipo, que
   * es el caso de la obra de una sola ruta.
   */
  conditionGroupId?: number | null
  source: DumperSource
  // Mutually exclusive: dealId+legacyOperatorId (Retri) OR operationId+transportAssetId+operatorId (SaaS)
  dealId?: number
  legacyOperatorId?: number
  transportAssetId?: number
  operationId?: number
  tripDate: string
  remisionNumber: string
  remisionSource?: DumperRemisionSource
  realM3?: number  // depende de unit (VIAJE no requiere)
  realTon?: number // requerido si alguna condition es TONELADA o TONELADA_KM
  operatorId?: number
  loadingZone?: string
  unloadingZone?: string
  tripCount?: number
  distanceKm?: number
  materialType?: string
  customIncomeRate?: number
  customExpenseRate?: number
  customMaterialRate?: number
  photoUrl?: string
  signMode?: DumperSignMode
  residentId?: number
  registeredByResidentId?: number
  residentObservation?: string
  driverSignatureUrl?: string
  machineryLicensePlate?: string
  machineryCubicCapacityM3?: number
}

export interface UpdateDumperHourmeterPayload {
  tripDate?: string
  remisionNumber?: string
  tripCount?: number
  realM3?: number
  distanceKm?: number
  materialType?: string
  operatorId?: number
  customIncomeRate?: number
  customExpenseRate?: number
  customMaterialRate?: number
  photoUrl?: string
  residentObservation?: string
}

export interface DumperHourmetersListQuery {
  dealId?: number
  operationId?: number
  transportAssetId?: number
  status?: DumperStatus
  source?: DumperSource
  from?: string
  to?: string
  includeArchived?: boolean
}

export interface SignInPersonPayload {
  residentId: number
  observation?: string
}

export interface SignOtpPayload {
  residentId: number
  code: string
  observation?: string
}

export interface RejectDumperPayload {
  residentId: number
  rejectReason: string
  code?: string
}

// QR resolve response (RS-1337)
export interface QrResolveResponse {
  code: string
  machine: {
    id: number
    name: string
    type: string
    source: 'machinery' | 'subcontracted'
  }
  activeAsset?: {
    id: number
    kind: string
    // RS-1338 v4: dealId/operationId derivados via JOIN asset_conditions
    dealId?: number | null
    operationId?: number | null
  } | null
  activeOperation?: {
    id: number
    clientName: string
    isDumperFeatureEnable: boolean
    dumperDealType?: 'TRANSPORT_ONLY' | 'MATERIAL_PLUS_TRANSPORT' | null
  }
  // Retri-managed deal fallback (machinery legacy sin asset SaaS)
  activeDeal?: {
    dealId: number
    subcategoryId?: number
    subcategoryName?: string
    status: string
    companyId?: number
    clientName?: string
  } | null
  // Conductor asignado a la máquina (para residente que firma desde QR scan)
  assignedDriver?: {
    kind: 'saas'
    saasOperatorId: number
    name: string
    phone?: string
  } | {
    kind: 'legacy'
    legacyOperatorId: number
    name: string
    phone?: string
    doc?: string
  } | null
  dumperDealType?: 'TRANSPORT_ONLY' | 'MATERIAL_PLUS_TRANSPORT' | null
  nextRemisionNumber?: string | null
  licensePlate?: string | null
  transportAssetId?: number | null
  // RS-1338 v4: conditions role=MATERIAL del deal/operation (no más assets kind=MATERIAL_SUPPLIER)
  materialAssets?: import('./index').MaterialAssetSummary[]
  // Flat back-compat del primer asset — grupo de condiciones (RS-1338).
  conditionIncomeName?: string | null
  conditionGroupId?: number | null
  groupName?: string | null
  photoRequired?: boolean
  registrationFlow?: 'DUMPER_HOURMETER' | 'HOURMETER' | null
  // RS-1338: N assets posibles para la misma machinery (distintas tarifas).
  // Cada entry trae su contexto hidratado + asset anidado con dealId/operationId.
  activeAssets?: QrActiveAsset[]
}

export interface QrActiveAsset {
  assetId: number
  asset: {
    id: number
    kind: string
    assignedOperatorId?: number | null
    conditionIncomeId?: number
    conditionMaterialId?: number | null
    dealId?: number | null
    operationId?: number | null
  }
  conditionIncomeName: string | null
  // Grupo de condiciones (RS-1338): id + nombre compuesto + foto obligatoria.
  conditionGroupId?: number | null
  groupName?: string | null
  photoRequired?: boolean
  incomeRate: number | null
  incomeUnit: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
  // Units de transport/material — el form deriva needsRealM3/Trips/Distance.
  transportUnit?: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
  materialUnit?: 'VIAJE' | 'M3' | 'M3_KM' | 'TONELADA' | 'HORA' | 'TONELADA_KM' | null
  activeOperation?: QrResolveResponse['activeOperation']
  activeDeal?: QrResolveResponse['activeDeal']
  materialAssets: import('./index').MaterialAssetSummary[]
  assignedDriver?: QrResolveResponse['assignedDriver']
  dumperDealType: 'TRANSPORT_ONLY' | 'MATERIAL_PLUS_TRANSPORT' | null
  nextRemisionNumber: string | null
  transportAssetId: number | null
  isVolqueta: boolean
  // materialType del asset = el de su conditionMaterial (si tiene).
  materialType?: string | null
  // Nombre de obra del deal — mostrado en el picker de tarifa.
  buildingName?: string | null
  // Residente fijo del asset (opcional). Si presente, prefilla form.residentId
  // y oculta el picker; si null, el operario escoge del pool del deal.
  residentId?: number | null
  residentName?: string | null
  residentPhone?: string | null
  // Zonas pactadas en la TRANSPORT condition del asset — prefill del form.
  loadingZone?: string | null
  unloadingZone?: string | null
}

export const DUMPER_STATUS_LABEL: Record<DumperStatus, string> = {
  PENDIENTE: 'Pendiente firma',
  FIRMADO: 'Firmado',
  RECHAZADO: 'Rechazado',
  REGISTRADO: 'Registrado',
  EDITADO: 'Editado (reemplazado)',
}

export const DUMPER_STATUS_COLOR: Record<DumperStatus, string> = {
  PENDIENTE: 'bg-retri-orange/20 text-retri-orange',
  FIRMADO: 'bg-retri-green/20 text-retri-green',
  RECHAZADO: 'bg-retri-red/20 text-retri-red',
  REGISTRADO: 'bg-retri-blue/20 text-retri-blue',
  EDITADO: 'bg-retri-gray/20 text-retri-darkgray',
}
